<template>
  <a-modal
    :visible="visible"
    :title="$t('invoice.editAddressedTo')"
    @ok="handleOk"
    @cancel="handleCancel"
    :confirm-loading="loading"
  >
    <!-- Loading state for parents -->
    <div v-if="loadingParents" class="loading-parents">
      <a-skeleton active :paragraph="{ rows: 2 }" />
      <p class="loading-text">{{ $t('invoice.loadingParentsInfo') }}</p>
    </div>

    <!-- Parent selection when parents are found -->
    <div v-else-if="parents.length > 0" class="parent-selection">
      <a-alert
        :message="parentAlertMessage"
        :description="parentAlertDescription"
        :type="parentAlertType"
        show-icon
        class="parent-alert"
      />

      <a-select
        v-if="parents.length > 1"
        v-model="selectedParentId"
        :placeholder="$t('invoice.selectParent')"
        class="parent-select"
        @change="handleParentSelection"
      >
        <a-select-option
          v-for="parent in parents"
          :key="parent._id"
          :value="parent._id"
        >
          {{ getParentDisplayName(parent) }}
        </a-select-option>
      </a-select>
    </div>

    <!-- Form -->
    <a-form :model="form" layout="vertical" v-if="!loadingParents">
      <a-row :gutter="16">
        <a-col :span="8">
          <a-form-item
            :label="$t('invoice.type')"
            :validate-status="getFieldStatus('type')"
            :help="errors.type"
          >
            <a-select
              v-model="form.type"
              :placeholder="$t('invoice.selectType')"
              @change="handleTypeChange"
              class="w-100"
            >
              <a-select-option
                v-for="type in addressTypes"
                :key="type.value"
                :value="type.value"
                :disabled="
                  type.value === 'ENTREPRISE' && selectedTypeFR === 'R'
                "
              >
                {{ type.label }}
              </a-select-option>
            </a-select>
          </a-form-item>
        </a-col>

        <a-col :span="8">
          <a-form-item
            :label="getRepresentativeLabel"
            :validate-status="getFieldStatus('representative')"
            :help="errors.representative || errors.MF"
          >
            <a-input
              v-if="form.type === 'ENTREPRISE'"
              class="w-100"
              v-model="form.MF"
              :placeholder="$t('invoice.enterMF')"
              @input="validate"
            />
            <a-select
              v-else-if="form.type === 'PARTICULAR'"
              class="w-100"
              v-model="form.representative"
              :placeholder="$t('invoice.selectRepresentative')"
              @change="handleRepresentativeChange"
            >
              <a-select-option
                v-for="rep in representatives"
                :key="rep.value"
                :value="rep.value"
              >
                {{ rep.label }}
              </a-select-option>
            </a-select>
          </a-form-item>
        </a-col>

        <a-col :span="8">
          <a-form-item
            :label="$t('invoice.addressedToName')"
            :validate-status="getFieldStatus('name')"
            :help="errors.name"
          >
            <a-input
              class="w-100"
              v-model="form.name"
              :placeholder="$t('invoice.enterName')"
              :disabled="isNameDisabled"
              @input="validate"
            />
          </a-form-item>
        </a-col>
      </a-row>
    </a-form>
  </a-modal>
</template>

<script>
import apiClient from "@/services/axios";

export default {
  name: "AddressedToModal",

  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    initialData: {
      type: Object,
      default: () => ({}),
    },
    selectedTypeFR: {
      type: String,
      default: "R",
    },
    fatherName: {
      type: String,
      default: "",
    },
    motherName: {
      type: String,
      default: "",
    },
    selectedStudentIds: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      form: this.getDefaultForm(),
      errors: this.getEmptyErrors(),
      loading: false,
      loadingParents: false,
      parents: [],
      selectedParentId: null,
    };
  },

  computed: {
    // Constants
    addressTypes() {
      return [
        { value: "PARTICULAR", label: this.$t("invoice.particular") },
        { value: "ENTREPRISE", label: this.$t("invoice.entreprise") },
      ];
    },

    representatives() {
      return [
        { value: "FATHER", label: this.$t("invoice.father") },
        { value: "MOTHER", label: this.$t("invoice.mother") },
        { value: "OTHER", label: this.$t("invoice.other") },
      ];
    },

    getRepresentativeLabel() {
      return this.form.type === "ENTREPRISE"
        ? this.$t("invoice.MF")
        : this.$t("invoice.representative");
    },

    isNameDisabled() {
      return (
        this.form.type === "PARTICULAR" &&
        (this.form.representative === "FATHER" ||
          this.form.representative === "MOTHER")
      );
    },

    parentAlertMessage() {
      return this.parents.length > 1
        ? this.$t("invoice.multipleParentsFound")
        : this.$t("invoice.parentFound");
    },

    parentAlertDescription() {
      return this.parents.length > 1
        ? this.$t("invoice.selectParentForDocument")
        : `${this.$t("invoice.documentAddressedTo")}: ${this.getParentDisplayName(this.parents[0])}`;
    },

    parentAlertType() {
      return this.parents.length > 1 ? "info" : "success";
    },

    currentParent() {
      if (!this.selectedParentId || !this.parents.length) return null;
      return (
        this.parents.find((parent) => parent._id === this.selectedParentId) ||
        this.parents[0]
      );
    },
  },

  watch: {
    visible(newValue) {
      if (newValue) {
        this.initializeForm();
        this.emitDefaults();
      }
    },

    selectedStudentIds: {
      handler(newIds, oldIds) {
        if (JSON.stringify(newIds) !== JSON.stringify(oldIds)) {
          if (newIds?.length > 0) {
            this.fetchParents();
          } else {
            this.resetParentsData();
          }
        }
      },
      deep: true,
      immediate: true,
    },
  },

  methods: {
    // Event handlers
    handleOk() {
      this.validate();
      if (this.isFormValid()) {
        this.$emit("ok", { ...this.form });
      }
      this.loading = false;
    },

    handleCancel() {
      this.$emit("cancel");
    },

    handleTypeChange(value) {
      this.resetFieldsByType(value);
      this.validate();
    },

    handleRepresentativeChange(value) {
      this.form.name = this.getRepresentativeName(value);
      this.validate();
    },

    handleParentSelection(value) {
      this.selectedParentId = value;
      this.updateNameFromRepresentative();
      this.validate();
    },

    // API methods
    async fetchParents() {
      if (!this.selectedStudentIds?.length) {
        this.resetParentsData();
        return;
      }

      this.loadingParents = true;
      try {
        const response = await apiClient.post("/parents/filter", {
          query: { students: { $in: this.selectedStudentIds } },
          aggregation: {
            $project: {
              _id: 1,
              father: { firstName: 1, lastName: 1 },
              mother: { firstName: 1, lastName: 1 },
            },
          },
        });

        this.parents = response.data;
        this.selectedParentId =
          response.data.length > 0 ? response.data[0]._id : null;

        if (response.data.length > 0) {
          this.setDefaultsFromParents();
        } else {
          this.resetToDefaults();
        }

        this.emitDefaults();
      } catch (error) {
        console.error("Error fetching parents:", error);
        this.$message.error(this.$t("invoice.errorLoadingParents"));
      } finally {
        this.loadingParents = false;
      }
    },

    // Form management
    initializeForm() {
      if (this.initialData && Object.keys(this.initialData).length > 0) {
        this.form = { ...this.initialData };
      } else if (this.parents.length > 0) {
        this.setDefaultsFromParents();
      } else {
        this.resetToDefaults();
      }
      this.errors = this.getEmptyErrors();
    },

    setDefaultsFromParents() {
      if (!this.currentParent) {
        this.resetToDefaults();
        return;
      }

      this.form.type = "PARTICULAR";
      this.form.representative = "FATHER";
      this.form.name = this.getRepresentativeName("FATHER");
      this.clearErrors();
    },

    resetToDefaults() {
      this.form = {
        ...this.getDefaultForm(),
        name: this.fatherName,
      };
      this.clearErrors();
    },

    resetParentsData() {
      this.parents = [];
      this.selectedParentId = null;
      this.resetToDefaults();
      this.emitDefaults();
    },

    resetFieldsByType(type) {
      if (type === "PARTICULAR") {
        this.form.MF = "";
        this.form.representative = "FATHER";
        this.form.name = this.getRepresentativeName("FATHER");
        this.clearErrors(["MF"]);
      } else if (type === "ENTREPRISE") {
        this.form.representative = "";
        this.form.name = "";
        this.clearErrors(["representative", "name"]);
      }
    },

    updateNameFromRepresentative() {
      if (
        this.form.representative === "FATHER" ||
        this.form.representative === "MOTHER"
      ) {
        this.form.name = this.getRepresentativeName(this.form.representative);
      } else {
        this.form.name = "";
      }
    },

    // Utility methods
    getParentDisplayName(parent) {
      if (!parent) return this.$t("invoice.noParentFound");

      const parts = [];
      if (parent.father?.firstName) {
        parts.push(
          `${this.$t("invoice.fatherLabel")}: ${parent.father.firstName} ${parent.father.lastName}`
        );
      }
      if (parent.mother?.firstName) {
        parts.push(
          `${this.$t("invoice.motherLabel")}: ${parent.mother.firstName} ${parent.mother.lastName}`
        );
      }

      return parts.length > 0
        ? parts.join(" | ")
        : this.$t("invoice.parentMissingInfo");
    },

    getRepresentativeName(representative) {
      const parent = this.currentParent;

      if (representative === "FATHER") {
        return parent?.father?.firstName
          ? `${parent.father.firstName} ${parent.father.lastName}`
          : this.fatherName;
      }

      if (representative === "MOTHER") {
        return parent?.mother?.firstName
          ? `${parent.mother.firstName} ${parent.mother.lastName}`
          : this.motherName;
      }

      return "";
    },

    emitDefaults() {
      this.$emit("defaults-set", { ...this.form });
    },

    // Validation
    validate() {
      this.errors = this.validateForm(this.form);
    },

    validateForm(formData) {
      const errors = this.getEmptyErrors();

      if (!formData.type) {
        errors.type = this.$t("invoice.typeRequired");
      }

      if (!formData.name?.trim()) {
        errors.name = this.$t("invoice.nameRequired");
      }

      if (formData.type === "PARTICULAR" && !formData.representative) {
        errors.representative = this.$t("invoice.representativeRequired");
      } else if (formData.type === "ENTREPRISE" && !formData.MF?.trim()) {
        errors.MF = this.$t("invoice.MFRequired");
      }

      return errors;
    },

    isFormValid() {
      return (
        !Object.values(this.errors).some((error) => error) &&
        this.form.name &&
        this.form.type
      );
    },

    getFieldStatus(field) {
      return this.errors[field] ? "error" : "";
    },

    clearErrors(fields = null) {
      if (fields) {
        fields.forEach((field) => {
          this.errors[field] = "";
        });
      } else {
        this.errors = this.getEmptyErrors();
      }
    },

    // Default data
    getDefaultForm() {
      return {
        name: "",
        type: "PARTICULAR",
        representative: "FATHER",
        MF: "",
      };
    },

    getEmptyErrors() {
      return {
        name: "",
        type: "",
        representative: "",
        MF: "",
      };
    },
  },
};
</script>

<style scoped>
.w-100 {
  width: 100%;
}

.loading-parents {
  text-align: center;
  padding: 20px;
}

.loading-text {
  margin-top: 10px;
  color: #555;
}

.parent-selection {
  margin-bottom: 20px;
}

.parent-alert {
  margin-bottom: 15px;
}

.parent-select {
  width: 100%;
}
</style>
